import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  SAMPLE_LOCAL_SWITCH_PURGE_CONFIRM,
  sampleService,
  type SampleLocalSwitchPurgePreview,
} from '../../services/sample.service';

type SampleLocalSwitchPurgeModalProps = {
  show: boolean;
  onCancel: () => void;
  onConfirmedAndPurged: (result: {
    deletedSamples: number;
    deletedStorageRooms: number;
    deletedUsageRecords: number;
  }) => void;
};

/**
 * クラウド DB → ローカル Zip 切替時の永久削除確認。
 * 自分の所有サンプル等のみ。取り消し不可。
 */
export default function SampleLocalSwitchPurgeModal({
  show,
  onCancel,
  onConfirmedAndPurged,
}: SampleLocalSwitchPurgeModalProps) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<SampleLocalSwitchPurgePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [ackIrreversible, setAckIrreversible] = useState(false);
  const [ackCollaborators, setAckCollaborators] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) {
      setPreview(null);
      setPreviewError(null);
      setAckIrreversible(false);
      setAckCollaborators(false);
      setConfirmText('');
      setSubmitError(null);
      setSubmitting(false);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);
    void sampleService
      .previewPurgeOwnedCloudForLocalSwitch()
      .then((res) => {
        if (!cancelled) setPreview(res.data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg =
            (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
              ?.message ||
            t('sample.local_switch_purge_preview_failed', {
              defaultValue: '削除対象の件数を取得できませんでした。',
            });
          setPreviewError(String(msg));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, t]);

  const confirmOk = confirmText.trim() === SAMPLE_LOCAL_SWITCH_PURGE_CONFIRM;
  const canSubmit =
    !!preview &&
    !loadingPreview &&
    !previewError &&
    ackIrreversible &&
    ackCollaborators &&
    confirmOk &&
    !submitting;

  const onSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    void sampleService
      .purgeOwnedCloudForLocalSwitch(SAMPLE_LOCAL_SWITCH_PURGE_CONFIRM)
      .then((res) => {
        onConfirmedAndPurged(res.data);
      })
      .catch((e: unknown) => {
        const msg =
          (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message ||
          t('sample.local_switch_purge_failed', {
            defaultValue: 'クラウドデータの削除に失敗しました。切替は中止しました。',
          });
        setSubmitError(String(msg));
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal show={show} onHide={onCancel} backdrop="static" keyboard={!submitting} centered>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title className="text-danger">
          {t('sample.local_switch_purge_title', {
            defaultValue: 'クラウド DB の自分のデータを永久削除します',
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="danger" className="mb-3">
          {t('sample.local_switch_purge_lead', {
            defaultValue:
              'ローカル Zip に切り替えると、サーバー上の「あなたが所有する」サンプル・関連使用記録・空になった保管庫を永久削除します。取り消しできません。',
          })}
        </Alert>
        <ul className="small mb-3">
          <li>
            {t('sample.local_switch_purge_scope', {
              defaultValue: '削除対象はログイン中のあなたが所有するデータのみです（他人のサンプルは消しません）。',
            })}
          </li>
          <li>
            {t('sample.local_switch_purge_collab', {
              defaultValue:
                '共有URLや共同利用で第三者が見ていたあなたのサンプルも、サーバー上から消えます。',
            })}
          </li>
          <li>
            {t('sample.local_switch_purge_local_note', {
              defaultValue: 'ローカル Zip 側のデータとは別です。この削除はクラウド正本のみです。',
            })}
          </li>
        </ul>

        {loadingPreview ? (
          <div className="d-flex align-items-center gap-2 text-muted small mb-3">
            <Spinner size="sm" animation="border" />
            {t('common.loading', { defaultValue: '読み込み中...' })}
          </div>
        ) : null}
        {previewError ? (
          <Alert variant="warning" className="mb-3">
            {previewError}
          </Alert>
        ) : null}
        {preview ? (
          <Alert variant="secondary" className="mb-3 py-2 small">
            {t('sample.local_switch_purge_counts', {
              samples: preview.sampleCount,
              usages: preview.usageRecordCount,
              rooms: preview.storageRoomCount,
              defaultValue:
                '削除予定: サンプル {{samples}} 件 · 使用記録 {{usages}} 件 · 所有保管庫 {{rooms}} 件',
            })}
          </Alert>
        ) : null}

        <Form.Check
          type="checkbox"
          id="purge-ack-irreversible"
          className="mb-2"
          checked={ackIrreversible}
          disabled={submitting}
          onChange={(e) => setAckIrreversible(e.target.checked)}
          label={t('sample.local_switch_purge_ack_irreversible', {
            defaultValue: '取り消しできないことを理解しました',
          })}
        />
        <Form.Check
          type="checkbox"
          id="purge-ack-collab"
          className="mb-3"
          checked={ackCollaborators}
          disabled={submitting}
          onChange={(e) => setAckCollaborators(e.target.checked)}
          label={t('sample.local_switch_purge_ack_collab', {
            defaultValue: '共同利用者・共有URL閲覧者への影響を理解しました',
          })}
        />
        <Form.Group className="mb-2" controlId="purge-confirm-text">
          <Form.Label className="small fw-semibold">
            {t('sample.local_switch_purge_type_label', {
              phrase: SAMPLE_LOCAL_SWITCH_PURGE_CONFIRM,
              defaultValue: '確認のため「{{phrase}}」と入力してください',
            })}
          </Form.Label>
          <Form.Control
            type="text"
            autoComplete="off"
            value={confirmText}
            disabled={submitting}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={SAMPLE_LOCAL_SWITCH_PURGE_CONFIRM}
          />
        </Form.Group>
        {submitError ? (
          <Alert variant="danger" className="mb-0 mt-2 py-2 small">
            {submitError}
          </Alert>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={submitting}>
          {t('common.cancel', { defaultValue: 'キャンセル' })}
        </Button>
        <Button variant="danger" disabled={!canSubmit} onClick={onSubmit}>
          {submitting ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              {t('sample.local_switch_purge_working', { defaultValue: '削除中…' })}
            </>
          ) : (
            t('sample.local_switch_purge_submit', {
              defaultValue: '永久削除してローカル Zip へ切替',
            })
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
